import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { PaymentWalletCheckout, Topup, getUser } from "../services/api";
import { FaArrowLeft, FaTrash, FaWallet, FaTimes, FaSpinner, FaShoppingBag, FaPlus } from "react-icons/fa";
import { ShoppingCart } from "lucide-react";
import logo from "../assets/logo2.png";
import axios from "axios";
import ServiceSelectorModal from "../components/ServiceSelectorModal";

function SiteNavbar({ cartCount }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  return (
    <nav className="bg-white border-b border-gray-100 sticky top-0 z-50">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
        <a href="/" className="flex items-center gap-2.5">
          <div className="w-9 h-9 bg-white rounded-full flex items-center justify-center overflow-hidden shadow-sm border border-gray-100 shrink-0">
            <img src={logo} alt="UCH" className="w-7 h-7 object-contain" />
          </div>
          <div className="hidden sm:block">
            <p className="font-bold text-gray-900 text-sm leading-tight">UCH Self Service</p>
            <p className="text-gray-400 text-[10px]">University College Hospital</p>
          </div>
        </a>
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="text-sm text-gray-500 hover:text-[#0d3d2e] flex items-center gap-1.5 transition">
            <FaArrowLeft className="w-3 h-3" /> Back
          </button>
          <button onClick={() => navigate(user ? "/dashboard" : "/login")}
            className="text-sm font-semibold text-white bg-[#0d3d2e] hover:bg-[#0a3226] px-4 py-2 rounded-lg transition">
            {user ? "Dashboard" : "Sign in"}
          </button>
        </div>
      </div>
    </nav>
  );
}

export default function WalletPay() {
  const { cart, updateCartItem, removeCartItem, clearCart } = useCart();
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [isProcessing, setIsProcessing] = useState(false);
  const [serviceModalOpen, setServiceModalOpen] = useState(false);
  const [payFor, setPayFor] = useState("self");
  const [customerDetails, setCustomerDetails] = useState({
    payer_first_name: user?.first_name || "",
    payer_last_name: user?.last_name || "",
    payer_email: user?.email || "",
    payer_phone_number: user?.phone_number || "",
    patient_first_name: user?.first_name || "",
    patient_last_name: user?.last_name || "",
    patient_hospital_number: user?.kyc?.hospital_number || "",
    patient_email: user?.email || "",
    patient_phone_number: user?.phone_number || "",
    transaction_description: "",
  });
  const [error, setError] = useState("");
  const [showFundModal, setShowFundModal] = useState(false);
  const [fundAmount, setFundAmount] = useState("");
  const [fundLoading, setFundLoading] = useState(false);
  const [fundStatus, setFundStatus] = useState(null);

  const CHARGE_FEE = 100;
  const SERVICE_CHARGE = 100;
  const totalAmount = cart.reduce((s, i) => s + (parseFloat(i.price) || 0) * (parseInt(i.quantity) || 1), 0);
  const walletBalance = parseFloat(user?.wallet_balance || 0);
  const isInsufficient = walletBalance < totalAmount + CHARGE_FEE;
  const neededAmount = Math.max(0, Math.ceil(totalAmount + CHARGE_FEE - walletBalance));

  // Open service modal if cart empty on load
  useEffect(() => {
    if (cart.length === 0) setServiceModalOpen(true);
  }, []);

  // Always fetch the freshest wallet balance on entering this page
  useEffect(() => {
    getUser().then(updateUser).catch(() => {});
  }, []);

  const refreshBalance = () => getUser().then(updateUser).catch(() => {});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCustomerDetails(prev => {
      const next = { ...prev, [name]: value };
      if (payFor === "self") {
        if (name === "patient_first_name") next.payer_first_name = value;
        if (name === "patient_last_name") next.payer_last_name = value;
      }
      return next;
    });
  };

  const handlePayForChange = (val) => {
    setPayFor(val);
    if (val === "self") {
      setCustomerDetails(prev => ({
        ...prev,
        patient_first_name: user?.first_name || "",
        patient_last_name: user?.last_name || "",
        patient_hospital_number: user?.kyc?.hospital_number || "",
        patient_email: user?.email || "",
        patient_phone_number: user?.phone_number || "",
        payer_first_name: user?.first_name || "",
        payer_last_name: user?.last_name || "",
        payer_email: user?.email || "",
        payer_phone_number: user?.phone_number || "",
      }));
    } else {
      setCustomerDetails(prev => ({
        ...prev,
        patient_first_name: "",
        patient_last_name: "",
        patient_hospital_number: "",
        patient_email: "",
        patient_phone_number: "",
        payer_first_name: user?.first_name || "",
        payer_last_name: user?.last_name || "",
        payer_email: user?.email || "",
        payer_phone_number: user?.phone_number || "",
      }));
    }
  };

  const scrollToField = (id) => {
    const el = document.getElementById(id);
    if (el) { el.scrollIntoView({ behavior: "smooth", block: "center" }); el.focus(); }
  };

  const handlePay = async () => {
    const { patient_first_name, patient_last_name, patient_hospital_number,
      patient_email, patient_phone_number,
      payer_first_name, payer_last_name, payer_email, payer_phone_number } = customerDetails;
    if (!patient_first_name) { scrollToField("patient_first_name"); setError("Please fill in patient first name."); return; }
    if (!patient_last_name) { scrollToField("patient_last_name"); setError("Please fill in patient last name."); return; }
    if (payFor === "self" && !patient_email) { scrollToField("patient_email"); setError("Please fill in patient email."); return; }
    if (payFor === "self" && !patient_phone_number) { scrollToField("patient_phone_number"); setError("Please fill in patient phone number."); return; }
    if (!payer_first_name) { scrollToField("payer_first_name"); setError("Please fill in payer first name."); return; }
    if (!payer_last_name) { scrollToField("payer_last_name"); setError("Please fill in payer last name."); return; }
    if (!payer_email) { scrollToField("payer_email"); setError("Please fill in payer email."); return; }
    if (!payer_phone_number) { scrollToField("payer_phone_number"); setError("Please fill in payer phone number."); return; }
    if (isInsufficient) { setShowFundModal(true); return; }

    setError(""); setIsProcessing(true);

    try {
      const payload = {
        items: cart.map(item => ({
          service: { id: item.id, name: item.name, description: item.description || "",
            head_code: null, service_type_id: null, price: (parseFloat(item.price)||0).toString(),
            amount: 0, service_charge: "no_free", fixed_price: "no",
            department: item.department || null, category: item.category || null },
          amount: parseFloat(item.price)||0, qty: item.quantity,
        })),
        customer_meta: {
          patient_first_name, patient_last_name, patient_hospital_number,
          patient_email, patient_phone_number,
          payer_first_name, payer_last_name, payer_email, payer_phone_number,
          transaction_description: customerDetails.transaction_description,
        },
        payment_method: "wallet",
        wallet_id: user?.id,
        charge_fee: CHARGE_FEE,
      };

      const response = await PaymentWalletCheckout(payload);
      clearCart();
      navigate("/wallet-pay/receipt", {
        state: {
          status: response.status === "pending" ? "pending" : response.status || "completed",
          paymentData: response,
          customerDetails,
          cart,
          totalAmount,
          chargeFee: CHARGE_FEE,
        },
      });
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Payment failed.";
      setError(msg);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleFundWallet = async () => {
    const amt = parseInt(fundAmount, 10);
    if (!amt || amt < 100) { setFundStatus({ type: "error", msg: "Minimum deposit is ₦100" }); return; }
    setFundLoading(true);
    try {
      const loadScript = (src) => new Promise((res, rej) => {
        if (window.RmPaymentEngine) { res(); return; }
        const s = document.createElement("script"); s.src = src; s.onload = res; s.onerror = rej;
        document.body.appendChild(s);
      });
      await loadScript("https://login.remita.net/payment/v1/remita-pay-inline.bundle.js");
      const res = await Topup({ amount: amt, payment_method: "remita" });
      const grossAmount = parseFloat(res.rrr_response?.gross_amount || res.gross_amount || amt + SERVICE_CHARGE);
      window.RmPaymentEngine.init({
        key: "QVZQTHw2MzU4MjA1MDYzfDFkN2UxYTgxNmUwMzQzMzhkMWM2ZmYzMTRiYzgxYmFlNDNkNzVlZTliNDI0ZWZhODQ0YWU5MWJkY2I1OTYwZTMyYWFkYzljNWMwZDIxMjcxOTA1NDI0YjA5OWI1MTlkNTM4MTk0MGI0YjMzMmE2M2E3NmFjY2ZhNzQyYjE0Y2Y1",
        transactionId: res.reference, processRrr: true,
        customerId: user?.email, firstName: user?.first_name || "", lastName: user?.last_name || "", email: user?.email,
        amount: grossAmount, narration: "Wallet top-up",
        extendedData: { customFields: [{ name: "rrr", value: res.rrr_response?.RRR }] },
        onSuccess: async (data) => {
          await axios.get(`https://api.uchselfservice.net/api/express/callback/top-up/remita/?transaction_id=${data.transactionId}&rrr=${res.rrr_response?.RRR}&amount=${grossAmount}`);
          await refreshBalance();
          setFundStatus({ type: "success", msg: "Wallet funded successfully!" });
          setTimeout(() => { setShowFundModal(false); setFundStatus(null); setFundAmount(""); }, 1800);
        },
        onError: () => setFundStatus({ type: "error", msg: "Payment failed." }),
        onClose: () => setFundStatus({ type: "info", msg: "Payment window closed." }),
      }).showPaymentWidget();
    } catch { setFundStatus({ type: "error", msg: "Failed to initialize payment." }); }
    finally { setFundLoading(false); }
  };

  const cartCount = cart.reduce((s, i) => s + i.quantity, 0);

  return (
    <div className="min-h-screen bg-[#f5f5f0] flex flex-col">
      <SiteNavbar cartCount={cartCount} />

      <div className="flex-1 max-w-5xl w-full mx-auto px-4 sm:px-6 py-8 space-y-6">

        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-black text-gray-900">Wallet Payment</h1>
            <p className="text-sm text-gray-500 mt-0.5">Review your services and complete payment from your wallet</p>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">Balance:</span>
            <span className="font-bold text-[#0d3d2e]">₦{walletBalance.toLocaleString("en-NG", { minimumFractionDigits: 2 })}</span>
          </div>
        </div>

        {cart.length === 0 ? (
          /* Empty cart state */
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-16 text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <FaShoppingBag className="text-gray-300 w-8 h-8" />
            </div>
            <h3 className="text-lg font-bold text-gray-900 mb-2">Your cart is empty</h3>
            <p className="text-gray-500 text-sm mb-6">Select hospital services to add to your cart</p>
            <button onClick={() => setServiceModalOpen(true)}
              className="bg-[#0d3d2e] hover:bg-[#0a3226] text-white font-semibold px-6 py-3 rounded-xl transition text-sm flex items-center gap-2 mx-auto">
              <FaShoppingBag className="w-4 h-4" /> Browse Services
            </button>
          </div>
        ) : (
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Left: Cart + Patient form */}
            <div className="lg:col-span-2 space-y-5">

              {/* Cart items */}
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-50">
                  <h2 className="font-bold text-gray-900 flex items-center gap-2">
                    <FaShoppingBag className="text-[#0d3d2e] w-4 h-4" />
                    Services ({cartCount})
                  </h2>
                  <button onClick={() => setServiceModalOpen(true)}
                    className="text-xs text-[#0d3d2e] font-semibold border border-green-200 px-3 py-1.5 rounded-lg hover:bg-green-50 transition flex items-center gap-1.5">
                    <FaPlus className="w-2.5 h-2.5" /> Add more
                  </button>
                </div>
                <div className="divide-y divide-gray-50">
                  {cart.map(item => (
                    <div key={item.id} className="flex items-center gap-3 px-5 py-4">
                      <div className="w-9 h-9 bg-green-50 rounded-xl flex items-center justify-center shrink-0">
                        <FaShoppingBag className="text-[#0d3d2e] w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-900 truncate">{item.name}</p>
                        <p className="text-xs text-gray-400">₦{(parseFloat(item.price)||0).toLocaleString()} each</p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <div className="flex items-center gap-1.5 bg-gray-50 border border-gray-100 rounded-lg px-2 py-1">
                          <button onClick={() => updateCartItem(item.id, item.quantity - 1)} className="w-5 h-5 flex items-center justify-center text-gray-600 hover:text-[#0d3d2e] font-bold">−</button>
                          <span className="text-sm font-bold text-gray-900 min-w-[20px] text-center">{item.quantity}</span>
                          <button onClick={() => updateCartItem(item.id, item.quantity + 1)} className="w-5 h-5 flex items-center justify-center text-gray-600 hover:text-[#0d3d2e] font-bold">+</button>
                        </div>
                        <p className="text-sm font-bold text-gray-900 min-w-[70px] text-right">₦{((parseFloat(item.price)||0)*(parseInt(item.quantity)||1)).toLocaleString()}</p>
                        <button onClick={() => removeCartItem(item.id)} className="text-red-400 hover:text-red-600 p-1 transition">
                          <FaTrash className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Patient Details */}
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 sm:p-6">
                <h2 className="font-bold text-gray-900 mb-5">Patient Information</h2>

                {/* Pay for */}
                <div className="flex gap-3 mb-5">
                  {["self", "someone_else"].map(v => (
                    <button key={v} onClick={() => handlePayForChange(v)}
                      className={`flex-1 py-2.5 rounded-xl text-sm font-semibold border-2 transition ${payFor === v ? "border-[#0d3d2e] bg-green-50 text-[#0d3d2e]" : "border-gray-100 bg-gray-50 text-gray-600 hover:border-gray-200"}`}>
                      {v === "self" ? "Paying for myself" : "Paying for someone"}
                    </button>
                  ))}
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  {[
                    { label: "Patient First Name", name: "patient_first_name", type: "text", placeholder: "First name", required: true },
                    { label: "Patient Last Name", name: "patient_last_name", type: "text", placeholder: "Last name", required: true },
                    { label: "Hospital Number (optional)", name: "patient_hospital_number", type: "text", placeholder: "e.g. UCH-123456 (if available)", colSpan: true, required: false },
                    { label: "Patient Email", name: "patient_email", type: "email", placeholder: "patient@email.com", required: payFor === "self" },
                    { label: "Patient Phone Number", name: "patient_phone_number", type: "tel", placeholder: "08xxxxxxxxx", required: payFor === "self" },
                  ].map(f => (
                    <div key={f.name} className={f.colSpan ? "sm:col-span-2" : ""}>
                      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">{f.label}{f.required ? " *" : " (optional)"}</label>
                      <input id={f.name} type={f.type} name={f.name} value={customerDetails[f.name]} onChange={handleChange}
                        placeholder={f.placeholder}
                        readOnly={f.readOnly}
                        className={`w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-green-500 transition ${f.readOnly ? "bg-gray-100 text-gray-500 cursor-not-allowed" : "bg-gray-50 focus:bg-white"}`} />
                    </div>
                  ))}
                </div>
              </div>

              {/* Payer Details */}
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 sm:p-6">
                <h2 className="font-bold text-gray-900 mb-5">Payer Information</h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  {[
                    { label: "First Name", name: "payer_first_name", type: "text", readOnly: payFor === "self" },
                    { label: "Last Name", name: "payer_last_name", type: "text", readOnly: payFor === "self" },
                    { label: "Email Address", name: "payer_email", type: "email" },
                    { label: "Phone Number", name: "payer_phone_number", type: "tel" },
                    { label: "Description (optional)", name: "transaction_description", type: "text", colSpan: true },
                  ].map(f => (
                    <div key={f.name} className={f.colSpan ? "sm:col-span-2" : ""}>
                      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">{f.label}</label>
                      <input id={f.name} type={f.type} name={f.name} value={customerDetails[f.name]} onChange={handleChange}
                        readOnly={f.readOnly}
                        className={`w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-green-500 transition ${f.readOnly ? "bg-gray-100 text-gray-500 cursor-not-allowed" : "bg-gray-50 focus:bg-white"}`} />
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Right: Order summary + Pay button */}
            <div className="space-y-4">
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 sticky top-24">
                <h3 className="font-bold text-gray-900 mb-4">Order Summary</h3>

                <div className="space-y-2.5 mb-5">
                  {cart.map(item => (
                    <div key={item.id} className="flex justify-between text-sm">
                      <span className="text-gray-600 truncate pr-2">{item.name} ×{item.quantity}</span>
                      <span className="font-semibold text-gray-900 shrink-0">₦{((parseFloat(item.price)||0)*(parseInt(item.quantity)||1)).toLocaleString()}</span>
                    </div>
                  ))}
                </div>

                <div className="border-t border-gray-100 pt-4 space-y-2.5 text-sm">
                  <div className="flex justify-between text-gray-600">
                    <span>Subtotal</span>
                    <span className="font-semibold text-gray-900">₦{totalAmount.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-gray-600">
                    <span>Service charge</span>
                    <span className="font-semibold text-gray-900">₦{CHARGE_FEE.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between items-center pt-2 border-t border-gray-100">
                    <span className="font-bold text-gray-900">Total</span>
                    <span className="text-xl font-black text-[#0d3d2e]">₦{(totalAmount + CHARGE_FEE).toLocaleString()}</span>
                  </div>
                </div>

                {/* Wallet balance status */}
                <div className={`mt-4 px-3 py-2.5 rounded-xl text-xs font-medium flex items-center gap-2 ${isInsufficient ? "bg-red-50 text-red-700 border border-red-100" : "bg-green-50 text-green-700 border border-green-100"}`}>
                  <FaWallet className="w-3.5 h-3.5 shrink-0" />
                  <span>
                    Wallet: ₦{walletBalance.toLocaleString()}
                    {isInsufficient ? ` · Need ₦${neededAmount.toLocaleString()} more` : " · Sufficient"}
                  </span>
                </div>

                {error && (
                  <div className="mt-3 bg-red-50 border border-red-200 text-red-700 text-xs px-3 py-2.5 rounded-xl">{error}</div>
                )}

                <button
                  onClick={isInsufficient ? () => { setFundAmount(String(neededAmount)); setShowFundModal(true); } : handlePay}
                  disabled={isProcessing}
                  className={`mt-4 w-full py-3.5 rounded-xl font-semibold text-sm transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 ${isInsufficient ? "bg-orange-500 hover:bg-orange-600 text-white" : "bg-[#0d3d2e] hover:bg-[#0a3226] text-white"}`}>
                  {isProcessing ? <><FaSpinner className="animate-spin" /> Processing...</> :
                   isInsufficient ? <><FaWallet /> Fund & Pay</> : <><FaWallet /> Pay with Wallet</>}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Service Selector Modal */}
      <ServiceSelectorModal
        open={serviceModalOpen}
        onClose={() => setServiceModalOpen(false)}
        redirectTo="/wallet-pay"
      />

      {/* Fund Wallet Modal */}
      {showFundModal && (
        <div className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center p-0 sm:p-4">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowFundModal(false)} />
          <div className="relative bg-white w-full sm:max-w-md rounded-t-3xl sm:rounded-2xl shadow-2xl overflow-hidden">
            <div className="bg-gradient-to-br from-[#0d3d2e] to-[#0f4a38] px-6 py-5 flex items-center justify-between">
              <div>
                <h3 className="text-white font-bold text-lg">Fund Wallet</h3>
                <p className="text-green-300 text-xs mt-0.5">You need ₦{neededAmount.toLocaleString()} more to complete payment</p>
              </div>
              <button onClick={() => setShowFundModal(false)} className="w-8 h-8 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition">
                <FaTimes className="w-3.5 h-3.5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-semibold text-sm">₦</span>
                <input type="number" value={fundAmount} onChange={e => { setFundAmount(e.target.value); setFundStatus(null); }}
                  placeholder="Enter amount" min="100"
                  className="w-full pl-8 pr-4 py-3 border border-gray-200 rounded-xl text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-green-500" />
              </div>
              {fundAmount && parseInt(fundAmount) >= 100 && (
                <div className="text-xs text-gray-500 flex justify-between bg-gray-50 rounded-lg px-3 py-2">
                  <span>Deposit: <strong>₦{parseInt(fundAmount || 0).toLocaleString()}</strong></span>
                  <span>+ ₦{SERVICE_CHARGE} charge</span>
                  <span>Total: <strong>₦{(parseInt(fundAmount || 0) + SERVICE_CHARGE).toLocaleString()}</strong></span>
                </div>
              )}
              {fundStatus && (
                <div className={`px-4 py-3 rounded-xl text-sm font-medium ${fundStatus.type === "success" ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"}`}>
                  {fundStatus.msg}
                </div>
              )}
              <button onClick={handleFundWallet} disabled={fundLoading || !fundAmount || parseInt(fundAmount) < 100}
                className="w-full bg-[#0d3d2e] hover:bg-[#0a3226] text-white font-semibold py-3.5 rounded-xl transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2">
                {fundLoading ? <><FaSpinner className="animate-spin" /> Processing...</> : "Pay with Remita"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
